import axios, { AxiosInstance } from 'axios';
import {
	getActiveCredentialSnapshot,
	getAuthSession,
	isSnapshotAdmissible,
	refreshAuthSession,
} from '@/features/auth/session';
import type { RequestCredentialSnapshot } from '@/features/auth/session';
import { resetAppState } from '@/app/appReset';
import { paths } from '@/app/router/path';
import { authUrl, dashUrl } from '@/legacy/config/env';
import { queryClient } from '@/legacy/lib/reactQuery';
import { USER_INFO_QUERY_KEY } from '@/features/space-user/api/constants';
import type { SpaceUserInfo } from '@/features/space-user/domain/types';
import { TIMEOUT_API } from '@/legacy/constants';
import { attachErrorReporting } from '@/shared/api-error';
import { logger } from '@/shared/logger';

// Guards resetAppState()+redirect to fire once per 401 burst: refreshAuthSession()'s
// rejected promise is shared by every awaiter, so each one's catch block would
// otherwise reset+redirect redundantly.
let isResetting = false;

export const attachInterceptors = (api: AxiosInstance) => {
	api.interceptors.request.use(
		(config) => {
			const { accessToken: token, isSuperUser } = getAuthSession();
			const userInfo = queryClient.getQueryData<SpaceUserInfo>([USER_INFO_QUERY_KEY]);
			const { language, languageAbbreviation, companyId } = userInfo ?? {};
			if (token) {
				config.headers.Authorization = `Bearer ${token}`;
				config.headers['x-platform'] = 'web';
			}
			// Bound to whichever scope is active AT REQUEST TIME — never the URL or
			// sessionStorage — so a later 401 retry refreshes/retries against this
			// exact scope even if the user switches company before the retry runs.
			(config as { _authSnapshot?: RequestCredentialSnapshot })._authSnapshot =
				getActiveCredentialSnapshot() ?? undefined;
			if (language) {
				config.headers['language'] = language;
			}
			if (languageAbbreviation) {
				config.headers['language_abbreviation'] = languageAbbreviation;
			}
			if (!(config.data instanceof FormData)) {
				config.headers['Content-Type'] = 'application/json';
			}

			if (Intl.DateTimeFormat().resolvedOptions().timeZone) {
				config.headers['timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
			}

			if (config.method?.toLowerCase() === 'get' && !(config as any).skipCacheBust) {
				const params = { ...(config.params || {}) };
				if (isSuperUser) {
					if (companyId != null) params._c = companyId;
				}

				config.params = params;

				config.headers['Cache-Control'] = 'no-cache, no-store, max-age=0, must-revalidate';
				config.headers['Pragma'] = 'no-cache';
			}
			return config;
		},
		(error) => Promise.reject(error),
	);

	api.interceptors.response.use(
		(response) => response,
		async (error) => {
			const originalRequest = error?.config;

			if (error?.response?.status === 401 && !originalRequest?._authRetried) {
				const snapshot = originalRequest?._authSnapshot as RequestCredentialSnapshot | undefined;

				// No snapshot, or its scope was already evicted (rejected token, logout):
				// nothing usable to refresh against — reject without retrying.
				if (!isSnapshotAdmissible(snapshot)) {
					return Promise.reject(error);
				}

				if (originalRequest) originalRequest._authRetried = true;

				try {
					const newAccessToken = await refreshAuthSession(snapshot!.scopeKey);
					if (originalRequest) {
						originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
						return api(originalRequest);
					}
				} catch (refreshError) {
					// Only reset the whole app when NO scope remains usable — a rejected
					// inactive scope (not the active one) must not log out a still-valid
					// active scope in another company/delegation.
					if (getActiveCredentialSnapshot() === null && !isResetting) {
						isResetting = true;
						// Forced logout is the normal end of an expired session — context for a
						// later real error, not an event worth its own Sentry quota.
						logger.breadcrumb('auth.session_refresh_failed', { error: String(refreshError) });
						resetAppState();
						window.location.href = paths.auth.login();
					}
					return Promise.reject(refreshError);
				}
			}

			return Promise.reject(error);
		},
	);
};

export const api = axios.create({
	baseURL: dashUrl(),
	timeout: TIMEOUT_API,
});

export const authApi = axios.create({
	baseURL: authUrl(),
	timeout: TIMEOUT_API,
});

attachInterceptors(api);
attachInterceptors(authApi);

// Registered AFTER the 401-refresh interceptor above — chain position (not a flag) is
// what makes reporting once-only. It only ever observes failures the refresh layer did
// not recover from. See design D-C.
attachErrorReporting(api);
attachErrorReporting(authApi);

// Keeping default for retrocompatibility
export default api;
